/**
 * Per-block layout variants — mirrors backend
 * /app/backend/marketplace/templates_v2.py::LAYOUT_VARIANTS and the layouts
 * supported in PremiumSectionRenderer.js.
 *
 * Each entry maps a block.type → array of { id, label, hint }
 * where `id` is what gets stored on block.layout and what the renderer reads.
 *
 * Iteration 141 — extended pool (Phase 1) so every template & user gets a
 * distinct look per block. Adding an entry here adds it to the in-builder
 * layout switcher panel automatically.
 */
export const LAYOUT_VARIANTS = {
  hero: [
    { id: 'cinematic-fullwidth', label: 'Cinematic',     hint: 'Full-bleed image + overlay headline' },
    { id: 'centered-minimal',    label: 'Centered',      hint: 'Tight centered text, no image' },
    { id: 'split-left',          label: 'Split L',       hint: 'Text left · image right' },
    { id: 'split-right',         label: 'Split R',       hint: 'Image left · text right' },
    { id: 'layered-editorial',   label: 'Editorial',     hint: 'Layered image + offset caption' },
    { id: 'video-hero',          label: 'Video',         hint: 'Background video' },
    { id: 'mountain-card',       label: 'Mountain Card', hint: 'Floating white card over scenic photo' },
    { id: 'sunset-form',         label: 'Sunset Form',   hint: 'Warm sky background + signup form' },
    { id: 'video-modal',         label: 'Video Modal',   hint: 'Big play button over a still image' },
    { id: 'water-fullbleed',     label: 'Aqua Bleed',    hint: 'Underwater/textured full-bleed photo' },
    { id: 'portrait-grid',       label: 'Portrait Grid', hint: 'Right-side product grid + left copy' },
    { id: 'card-on-photo',       label: 'Card on Photo', hint: 'White card sitting on top of photo' },
  ],
  gallery: [
    { id: 'grid-uniform',        label: 'Grid',         hint: 'Uniform square grid' },
    { id: 'masonry',             label: 'Masonry',      hint: 'Pinterest-style varied heights' },
    { id: 'lookbook',            label: 'Lookbook',     hint: 'Full-bleed editorial cells' },
    { id: 'horizontal-scroll',   label: 'Carousel',     hint: 'Horizontal swipe row' },
    { id: 'mosaic-asymmetric',   label: 'Mosaic',       hint: '6-photo asymmetric layout' },
    { id: 'violet-bg-masonry',   label: 'Violet BG',    hint: 'Masonry on solid violet background' },
    { id: 'filterable',          label: 'Filtered',     hint: 'Pill-filtered grid (Photos/Videos)' },
    { id: 'with-swatches',       label: 'Swatches',     hint: 'Product grid with color swatches' },
    { id: 'alternating-rows',    label: 'Alternating',  hint: 'Image+text alternating case studies' },
    { id: 'last-works-link',     label: 'Last Works',   hint: 'Violet bg + "See All Works" link' },
    { id: 'carousel-scroll',     label: 'Carousel',     hint: 'Swipeable horizontal image carousel' },
    { id: 'marquee-gallery',     label: 'Marquee',      hint: 'Auto-scrolling infinite gallery loop' },
  ],
  split: [
    { id: 'image-right',         label: 'Image R',      hint: 'Text left · image right' },
    { id: 'image-left',          label: 'Image L',      hint: 'Image left · text right' },
    { id: 'overlapping-image',   label: 'Overlap',      hint: 'Two stacked, overlapping images' },
    { id: 'asymmetric',          label: 'Asymmetric',   hint: 'Off-center editorial split' },
    { id: 'split-form',          label: 'Split + Form', hint: 'Photo left · form right' },
    { id: 'split-overlay',       label: 'Card Overlay', hint: 'Image with overlapping content card' },
  ],
  stats: [
    { id: 'kpi-row',             label: 'KPI Row',      hint: 'Inline numbers row' },
    { id: 'kpi-cards',           label: 'Cards',        hint: 'Individual stat cards' },
    { id: 'centered-counter',    label: 'Centered',     hint: 'One big centered counter' },
    { id: 'dark-violet-card',    label: 'Violet Card',  hint: 'Dark violet box with stats grid' },
    { id: 'side-stats',          label: 'Side Stats',   hint: 'Photo + side stats column' },
    { id: 'stats-with-mockup',   label: 'Stats + Mockup', hint: 'Stats grid + product/abstract mockup' },
  ],
  testimonials: [
    { id: 'three-up',            label: 'Three-up',     hint: '3-column quote grid' },
    { id: 'quote-banner',        label: 'Banner',       hint: 'Full-width hero quote' },
    { id: 'stacked',             label: 'Stacked',      hint: 'Vertical list' },
    { id: 'carousel',            label: 'Carousel',     hint: 'Swipeable cards' },
    { id: 'two-by-two-grid',     label: '2×2 Grid',     hint: 'Four reviews in a 2×2 grid' },
    { id: 'violet-2up',          label: 'Violet 2-up',  hint: 'Two reviews on dark violet' },
    { id: 'happy-clients-quadgrid', label: 'Happy Clients', hint: 'Dark violet 2×2 client testimonials' },
    { id: 'single-quote-portrait',label: 'Quote + Portrait', hint: 'Big quote + round portrait photo' },
    { id: 'author-bottom-card', label: 'Author Card',     hint: '3-up cards with author at bottom' },
  ],
  services: [
    { id: 'three-column-cards',  label: 'Cards',        hint: '3-column service cards' },
    { id: 'pricing-tabs',        label: 'Tabbed',       hint: 'Tabbed pricing (Free/Pro/Master)' },
    { id: 'pricing-highlighted-middle', label: 'Middle Pop', hint: 'Middle tier highlighted, flanked white' },
    { id: 'pricing-comparison-grid', label: 'Compare',  hint: 'Feature × tier comparison grid' },
    { id: 'pricing-dark-hero',  label: 'Dark Hero',     hint: 'Pricing on dark violet hero with image' },
    { id: 'pricing-membership', label: 'Membership',    hint: 'Image grid + 2-tier membership card' },
  ],
  features: [
    { id: 'three-col-grid',      label: 'Grid',         hint: '3-column icon grid' },
    { id: 'alternating-rows',    label: 'Alternating',  hint: 'Image-text alternating' },
    { id: 'icon-cards',          label: 'Icon cards',   hint: 'Square icon cards' },
    { id: 'icon-row',            label: 'Icon Row',     hint: 'Inline icon row + label' },
    { id: 'integration-cards',   label: 'Integrations', hint: '2-card integration callouts' },
    { id: 'phone-mockup',        label: 'Phone Mockup', hint: 'Feature copy + phone device' },
    { id: 'laptop-mockup',       label: 'Laptop',       hint: 'Feature copy + laptop device' },
    { id: 'six-icon-violet',     label: 'Violet 6-Up',  hint: '6-icon grid on dark violet' },
  ],
  editorial: [
    { id: 'centered-statement',  label: 'Statement',    hint: 'Centered manifesto text' },
    { id: 'pull-quote',          label: 'Pull-quote',   hint: 'Large emphasized quote' },
    { id: 'two-column-manifesto',label: 'Two-col',      hint: 'Two parallel columns' },
    { id: 'dark-violet-block',   label: 'Dark Block',   hint: 'Solid violet manifesto block' },
    { id: 'sub-card',            label: 'Sub Card',     hint: 'Aside sub-card variant' },
  ],
  cta: [
    { id: 'large-banner',        label: 'Banner',       hint: 'Full-bleed call-out' },
    { id: 'image-cta',           label: 'Image CTA',    hint: 'CTA over image' },
    { id: 'split-cta',           label: 'Split',        hint: 'CTA + secondary text' },
    { id: 'pill-bar',            label: 'Pill Bar',     hint: 'Slim full-width pill bar' },
    { id: 'social-pill-row',     label: 'Social Pills', hint: 'CTA + social network pills' },
    { id: 'mountain-overlay',    label: 'Mountain',     hint: 'Mountain photo + overlay CTA' },
    { id: 'video-modal-cta',     label: 'Play CTA',     hint: 'Big play button CTA' },
    { id: 'phone-on-sunset',     label: 'Phone Sunset', hint: 'Floating phone over sunset' },
    { id: 'balloons-asymmetric', label: 'Balloons',     hint: 'Asymmetric photo-side CTA' },
    { id: 'white-bordered-card', label: 'Bordered Card', hint: 'Tight bordered card with double CTA' },
    { id: 'video-strip-vertical',label: 'Video Strip',  hint: 'Vertical photo strip + play CTA' },
  ],
  team: [
    { id: 'card-grid',           label: 'Grid',         hint: 'Photo card grid' },
    { id: 'founder-spotlight',   label: 'Spotlight',    hint: 'Featured founder' },
    { id: 'palm-leaf-bg',        label: 'Palm Leaf',    hint: 'Botanical photo backdrop + card' },
    { id: 'square-cards',        label: 'Square Cards', hint: '2×4 square photo cards' },
    { id: 'violet-bg',           label: 'Violet BG',    hint: 'Round avatars on solid violet' },
  ],
  marquee: [
    { id: 'scrolling',           label: 'Scrolling',    hint: 'Marquee scroll (premium)' },
  ],
  form: [
    { id: 'contact',             label: 'Contact',      hint: 'Standard contact form' },
    { id: 'newsletter',          label: 'Newsletter',   hint: 'Single-email signup' },
    { id: 'application',         label: 'Application',  hint: 'Long-form application' },
    { id: 'donate',              label: 'Donate',       hint: 'Donation form' },
    { id: 'booking',             label: 'Booking',      hint: 'Scheduling intake form' },
    { id: 'auth-signup',         label: 'Sign Up',      hint: 'Email/password sign-up form' },
    { id: 'auth-signin',         label: 'Sign In',      hint: 'Email/password sign-in form' },
    { id: 'payment-method',      label: 'Payment',      hint: 'Credit-card payment form' },
    { id: 'billing-account',     label: 'Billing',      hint: 'Full billing-address form' },
  ],
  // NEW block types unlocked by Phase 2
  logos: [
    { id: 'row-grey',            label: 'Grey Row',     hint: 'Inline grey-out logo row' },
    { id: 'logo-cards-grid',     label: 'Logo Cards',   hint: '2×4 logo card grid' },
    { id: 'violet-trust-bar',    label: 'Violet Trust', hint: 'Dark violet "trusted by" bar' },
  ],
  data_table: [
    { id: 'pay-as-you-go',       label: 'Pay-as-you-go', hint: 'Striped pricing tier table' },
    { id: 'feature-spec',        label: 'Feature Spec',  hint: 'What\'s Inside spec table' },
    { id: 'comparison',          label: 'Compare',       hint: 'Tier comparison grid' },
  ],
  product_grid: [
    { id: 'three-col-cards',     label: '3-col Cards',  hint: 'Best-selling product grid' },
    { id: 'four-col-clean',      label: '4-col Clean',  hint: 'Latest products clean grid' },
    { id: 'with-color-swatches', label: 'Swatches',     hint: 'Product cards with color swatches' },
    { id: 'category-with-filter-sidebar', label: 'With Filters', hint: 'Sidebar filters + product grid' },
    { id: 'gradient-promo-twin', label: 'Promo Twin',    hint: 'Two side-by-side gradient promo cards' },
  ],
  product_detail: [
    { id: 'left-thumbs-right-info', label: 'Thumbs+Info', hint: 'Thumbs left · info right (PDP)' },
    { id: 'two-col-fashion',     label: 'Fashion PDP',   hint: 'Image left · purchase right' },
  ],
  cart_table: [
    { id: 'line-items',          label: 'Line Items',   hint: 'Cart line-items with qty stepper' },
  ],
};

export function getLayoutVariants(blockType) {
  return LAYOUT_VARIANTS[blockType] || [];
}

export function getCurrentLayout(block) {
  return block?.layout || null;
}
